import { useCallback, useEffect, useState } from "react";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { useT } from "../context/LangContext";
import { GALLERY } from "../lib/site";

const Lightbox = ({ index, onClose, onPrev, onNext, alt, closeLabel }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const img = GALLERY[index];
  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button onClick={onClose} aria-label={closeLabel} data-testid="lightbox-close" className="absolute top-5 right-5 rounded-full bg-bone/10 p-3 text-bone transition-colors hover:bg-bone/20">
        <X size={22} weight="bold" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="previous" data-testid="lightbox-prev" className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 rounded-full bg-bone/10 p-3 text-bone transition-colors hover:bg-bone/20">
        <CaretLeft size={24} weight="bold" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="next" data-testid="lightbox-next" className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 rounded-full bg-bone/10 p-3 text-bone transition-colors hover:bg-bone/20">
        <CaretRight size={24} weight="bold" />
      </button>
      <motion.img
        key={img.src}
        src={img.src}
        alt={`${alt} ${index + 1}`}
        className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-display text-xs tracking-widest text-bone/70">
        {index + 1} / {GALLERY.length}
      </span>
    </motion.div>
  );
};

export default function Immagini() {
  const { t } = useT();
  const c = t.gallery;
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);
  const next = useCallback(() => setActive((i) => (i + 1) % GALLERY.length), []);

  return (
    <>
      <PageHero kicker={c.heroKicker} titleLines={c.heroTitleLines} />
      <section className="py-10 md:py-14" data-testid="gallery-page">
        <div className="mx-auto max-w-7xl px-5 md:px-8 columns-1 sm:columns-2 lg:columns-3 gap-5">
          {GALLERY.map((img, i) => (
            <Reveal key={img.src} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
              <button
                type="button"
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
                className="group block w-full overflow-hidden rounded-2xl bg-surface text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                style={{ aspectRatio: `${img.w} / ${img.h}` }}
              >
                <img
                  src={img.src}
                  alt={`${c.altLabel} ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </section>
      <AnimatePresence>
        {active !== null && (
          <Lightbox index={active} onClose={close} onPrev={prev} onNext={next} alt={c.altLabel} closeLabel={c.closeLabel} />
        )}
      </AnimatePresence>
    </>
  );
}
